import React, { useState, useMemo } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Form,
  Input,
  Select,
  Modal,
  Switch,
  Popconfirm,
  Tooltip,
  Row,
  Col,
  Statistic,
  Tree,
  InputNumber,
  Alert,
  App,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CalculatorOutlined,
  SettingOutlined,
  SwapOutlined,
  ApartmentOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../services/api';

const { Title } = Typography;
const { Option } = Select;

const Units = () => {
  const { message } = App.useApp();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isConversionModalVisible, setIsConversionModalVisible] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [form] = Form.useForm();
  const [conversionForm] = Form.useForm();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [conversionResult, setConversionResult] = useState(null);
  const queryClient = useQueryClient();

  // Fetch units with filters
  const { data: units = [], isLoading } = useQuery(
    ['units', searchTerm, statusFilter, categoryFilter],
    async () => {
      try {
        const params = new URLSearchParams();
        if (searchTerm) params.append('search', searchTerm);
        if (statusFilter) params.append('status', statusFilter);
        if (categoryFilter) params.append('category', categoryFilter);

        const response = await api.get(`/admin/units/all?${params.toString()}`);
        return response.data?.data || response.data || [];
      } catch (error) {
        console.error('Failed to fetch units:', error);
        if (error.response?.status === 401) {
          window.location.href = '/login';
        }
        throw error;
      }
    },
    {
      refetchInterval: 30000,
      staleTime: 5 * 60 * 1000,
    },
  );

  // Client-side filtering fallback
  const filteredUnits = useMemo(() => {
    if (!Array.isArray(units)) return [];
    let filtered = [...units];

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (u) =>
          (u.name && u.name.toLowerCase().includes(searchLower)) ||
          (u.symbol && u.symbol.toLowerCase().includes(searchLower)) ||
          (u.description && u.description.toLowerCase().includes(searchLower)),
      );
    }

    if (statusFilter) {
      filtered = filtered.filter((u) => u.status === statusFilter);
    }

    if (categoryFilter) {
      filtered = filtered.filter((u) => u.category === categoryFilter);
    }

    return filtered;
  }, [units, searchTerm, statusFilter, categoryFilter]);

  // Create unit mutation
  const createUnitMutation = useMutation(
    async (unitData) => {
      const response = await api.post('/admin/units', unitData);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Unit created successfully');
        setIsModalVisible(false);
        form.resetFields();
        queryClient.invalidateQueries('units');
      },
      onError: (error) => {
        if (error.response?.status === 422) {
          // Validation error - show specific message
          const errorMessage = error.response?.data?.message || 'Validation failed';
          message.error(errorMessage);
        } else {
          message.error(error.response?.data?.message || 'Failed to create unit');
        }
      },
    },
  );

  // Update unit mutation
  const updateUnitMutation = useMutation(
    async ({ id, ...unitData }) => {
      const response = await api.put(`/admin/units/${id}`, unitData);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Unit updated successfully');
        setIsModalVisible(false);
        setEditingUnit(null);
        form.resetFields();
        queryClient.invalidateQueries('units');
      },
      onError: (error) => {
        if (error.response?.status === 422) {
          // Validation error - show specific message
          const errorMessage = error.response?.data?.message || 'Validation failed';
          message.error(errorMessage);
        } else {
          message.error(error.response?.data?.message || 'Failed to update unit');
        }
      },
    },
  );

  // Delete unit mutation
  const deleteUnitMutation = useMutation(
    async (id) => {
      await api.delete(`/admin/units/${id}`);
    },
    {
      onSuccess: () => {
        message.success('Unit deleted successfully');
        queryClient.invalidateQueries('units');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to delete unit');
      },
    },
  );

  // Conversion mutation
  const convertUnitsMutation = useMutation(
    async (conversionData) => {
      const response = await api.post('/admin/units/convert', conversionData);
      return response.data;
    },
    {
      onSuccess: (data) => {
        setConversionResult(data.data);
        message.success('Units converted successfully');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to convert units');
      },
    },
  );

  // Toggle unit status mutation
  const toggleStatusMutation = useMutation(
    async ({ id, status }) => {
      await api.patch(`/admin/units/${id}/toggle-status`, { status });
    },
    {
      onSuccess: () => {
        message.success('Unit status updated successfully');
        queryClient.invalidateQueries('units');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to update unit status');
      },
    },
  );

  const handleSubmit = async (values) => {
    try {
      if (editingUnit) {
        updateUnitMutation.mutate({ id: editingUnit.id, ...values });
      } else {
        createUnitMutation.mutate(values);
      }
    } catch (error) {
      console.error('Submit error:', error);
    }
  };

  const handleConversion = async (values) => {
    try {
      convertUnitsMutation.mutate(values);
    } catch (error) {
      console.error('Conversion error:', error);
    }
  };

  const handleEdit = (unit) => {
    setEditingUnit(unit);
    form.setFieldsValue(unit);
    setIsModalVisible(true);
  };

  const handleDelete = (id) => {
    deleteUnitMutation.mutate(id);
  };

  const handleToggleStatus = (unit) => {
    const newStatus = unit.status === 'active' ? 'inactive' : 'active';
    toggleStatusMutation.mutate({ id: unit.id, status: newStatus });
  };

  // Get root units for parent selection (excluding current unit when editing)
  const getRootUnits = () => {
    let rootUnits = units.filter((unit) => !unit.parent_unit_id);
    if (editingUnit) {
      // Exclude the current unit and all its descendants from parent selection
      const excludeIds = [editingUnit.id];
      const getDescendantIds = (unitId) => {
        const children = units.filter((u) => u.parent_unit_id === unitId);
        let ids = [];
        children.forEach((child) => {
          ids.push(child.id);
          ids = ids.concat(getDescendantIds(child.id));
        });
        return ids;
      };
      excludeIds.push(...getDescendantIds(editingUnit.id));
      rootUnits = rootUnits.filter((unit) => !excludeIds.includes(unit.id));
    }
    return rootUnits;
  };

  // Get child units for parent selection
  const getChildUnits = (parentId = null) => {
    if (!parentId) return [];
    return units.filter((unit) => unit.parent_unit_id === parentId);
  };

  // Build hierarchy tree data
  const buildHierarchyTree = () => {
    const rootUnits = getRootUnits();
    return rootUnits.map((unit) => ({
      title: (
        <div>
          <strong>{unit.name}</strong> ({unit.symbol})
          <br />
          <small>{unit.description}</small>
        </div>
      ),
      key: unit.id,
      children: buildChildNodes(unit.id),
    }));
  };

  const buildChildNodes = (parentId) => {
    const children = getChildUnits(parentId);
    return children.map((unit) => ({
      title: (
        <div>
          <strong>{unit.name}</strong> ({unit.symbol})
          <br />
          <small>
            1 {unit.parent_unit?.symbol} = {unit.conversion_factor} {unit.symbol}
          </small>
        </div>
      ),
      key: unit.id,
      children: buildChildNodes(unit.id),
    }));
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
      render: (name, record) => (
        <div>
          <span style={{ fontWeight: '500' }}>{name}</span>
          {record.parent_unit && (
            <div>
              <small style={{ color: '#666' }}>Child of: {record.parent_unit.name}</small>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Symbol',
      dataIndex: 'symbol',
      key: 'symbol',
      sorter: (a, b) => (a.symbol || '').localeCompare(b.symbol || ''),
      render: (symbol) => (
        <Tag color="blue" style={{ fontSize: '14px', padding: '4px 8px' }}>
          {symbol}
        </Tag>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      sorter: (a, b) => (a.category || '').localeCompare(b.category || ''),
      render: (category) => (
        <Tag
          color={
            category === 'weight'
              ? 'green'
              : category === 'volume'
                ? 'orange'
                : category === 'length'
                  ? 'purple'
                  : category === 'time'
                    ? 'cyan'
                    : category === 'quantity'
                      ? 'blue'
                      : 'default'
          }
        >
          {category?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Hierarchy',
      dataIndex: 'hierarchy_display',
      key: 'hierarchy_display',
      ellipsis: true,
      render: (hierarchy) => (
        <Tooltip title={hierarchy}>
          <span style={{ fontSize: '14px' }}>{hierarchy}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Conversion',
      dataIndex: 'conversion_factor',
      key: 'conversion_factor',
      render: (factor, record) => {
        if (!factor || !record.parent_unit) return 'N/A';
        return (
          <span style={{ fontSize: '14px' }}>
            1 {record.parent_unit.symbol} = {factor} {record.symbol}
          </span>
        );
      },
    },
    {
      title: 'Base Conversion',
      dataIndex: 'base_conversion',
      key: 'base_conversion',
      render: (conversion) => (
        <span style={{ fontSize: '14px' }}>{conversion ? `${conversion}x` : '1x'}</span>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (description) => (
        <Tooltip title={description}>
          <span>{description}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: (a, b) => (a.status || '').localeCompare(b.status || ''),
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>{status?.toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title={record.status === 'active' ? 'Deactivate' : 'Activate'}>
            <Switch
              size="small"
              checked={record.status === 'active'}
              onChange={() => handleToggleStatus(record)}
              loading={toggleStatusMutation.isLoading}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this unit?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
                loading={deleteUnitMutation.isLoading}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Calculate statistics
  const activeUnits = units.filter((u) => u.status === 'active').length;
  const rootUnits = units.filter((u) => !u.parent_unit_id).length;
  const childUnits = units.filter((u) => u.parent_unit_id).length;
  const categoryStats = units.reduce((acc, unit) => {
    acc[unit.category] = (acc[unit.category] || 0) + 1;
    return acc;
  }, {});

  return (
    <div>
      <Title level={2}>
        <CalculatorOutlined /> Units Management
      </Title>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Units"
              value={units.length}
              prefix={<CalculatorOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Active Units"
              value={activeUnits}
              prefix={<SettingOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Root Units"
              value={rootUnits}
              prefix={<ApartmentOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Child Units"
              value={childUnits}
              prefix={<InfoCircleOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Category Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24}>
          <Card title="Units by Category">
            <Row gutter={[16, 16]}>
              {Object.entries(categoryStats).map(([category, count]) => {
                const categoryKey = category || 'uncategorized';
                const displayName =
                  category === null || category === '' ? 'Uncategorized' : category?.toUpperCase();
                return (
                  <Col xs={24} sm={12} md={6} key={categoryKey}>
                    <Card size="small">
                      <Statistic
                        title={displayName}
                        value={count}
                        valueStyle={{
                          color:
                            categoryKey === 'weight'
                              ? '#52c41a'
                              : categoryKey === 'volume'
                                ? '#fa8c16'
                                : categoryKey === 'length'
                                  ? '#722ed1'
                                  : categoryKey === 'time'
                                    ? '#13c2c2'
                                    : categoryKey === 'quantity'
                                      ? '#1890ff'
                                      : '#666',
                        }}
                      />
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </Card>
        </Col>
      </Row>

      <Card>
        {/* Filters and Actions */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="Search units..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Filter by status"
              value={statusFilter}
              onChange={setStatusFilter}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Filter by category"
              value={categoryFilter}
              onChange={setCategoryFilter}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="quantity">Quantity</Option>
              <Option value="weight">Weight</Option>
              <Option value="volume">Volume</Option>
              <Option value="length">Length</Option>
              <Option value="time">Time</Option>
            </Select>
          </Col>
          <Col xs={24} sm={24} md={10} style={{ textAlign: 'right' }}>
            <Space>
              <Button icon={<SwapOutlined />} onClick={() => setIsConversionModalVisible(true)}>
                Convert Units
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditingUnit(null);
                  form.resetFields();
                  setIsModalVisible(true);
                }}
              >
                Add New Unit
              </Button>
            </Space>
          </Col>
        </Row>

        {/* Units Table */}
        <Table
          columns={columns}
          dataSource={filteredUnits}
          rowKey="id"
          loading={isLoading}
          pagination={{
            total: filteredUnits.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} units`,
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Hierarchy View */}
      <Card title="Unit Hierarchy" style={{ marginTop: 24 }}>
        <Alert
          message="Hierarchical Unit Structure"
          description="This view shows the relationship between units. For example: 1 Box = 20 Sub-Boxes = 240 Pieces"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Tree treeData={buildHierarchyTree()} defaultExpandAll showLine showIcon={false} />
      </Card>

      {/* Add/Edit Unit Modal */}
      <Modal
        title={editingUnit ? 'Edit Unit' : 'Add New Unit'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingUnit(null);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="name"
                label="Unit Name"
                rules={[
                  { required: true, message: 'Please enter unit name' },
                  { min: 2, message: 'Unit name must be at least 2 characters' },
                  { max: 50, message: 'Unit name cannot exceed 50 characters' },
                ]}
              >
                <Input placeholder="e.g., Box, Sub Box, Piece" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="symbol"
                label="Unit Symbol"
                rules={[
                  { required: true, message: 'Please enter unit symbol' },
                  { max: 20, message: 'Unit symbol cannot exceed 20 characters' },
                ]}
              >
                <Input
                  placeholder="e.g., box, subbox, pcs"
                  style={{ textTransform: 'uppercase' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="category"
                label="Unit Category"
                rules={[{ required: true, message: 'Please select unit category' }]}
              >
                <Select placeholder="Select unit category">
                  <Option value="quantity">Quantity</Option>
                  <Option value="weight">Weight</Option>
                  <Option value="volume">Volume</Option>
                  <Option value="length">Length</Option>
                  <Option value="time">Time</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="status"
                label="Status"
                initialValue="active"
                rules={[{ required: true, message: 'Please select status' }]}
              >
                <Select>
                  <Option value="active">Active</Option>
                  <Option value="inactive">Inactive</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="parent_unit_id"
                label="Parent Unit (Optional)"
                help="Select a parent unit to create a hierarchical relationship (e.g., Box -> Sub Box -> Piece)"
              >
                <Select
                  placeholder="Select parent unit"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {getRootUnits().map((unit) => (
                    <Option key={unit.id} value={unit.id}>
                      {unit.name} ({unit.symbol})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="conversion_factor"
                label="Conversion Factor"
                dependencies={['parent_unit_id']}
                rules={[
                  { type: 'number', min: 0.0001, message: 'Conversion factor must be positive' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (getFieldValue('parent_unit_id') && !value) {
                        return Promise.reject(
                          new Error('Conversion factor is required when parent unit is selected'),
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
                help="How many of this unit equal 1 parent unit (e.g., 20 sub-boxes = 1 box)"
              >
                <InputNumber
                  placeholder="e.g., 20"
                  min={0.0001}
                  step={0.0001}
                  precision={4}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Description"
            rules={[
              { required: true, message: 'Please enter unit description' },
              { max: 200, message: 'Description cannot exceed 200 characters' },
            ]}
          >
            <Input.TextArea
              rows={3}
              placeholder="Enter unit description"
              maxLength={200}
              showCount
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button
                onClick={() => {
                  setIsModalVisible(false);
                  setEditingUnit(null);
                  form.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={createUnitMutation.isLoading || updateUnitMutation.isLoading}
              >
                {editingUnit ? 'Update Unit' : 'Create Unit'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Unit Conversion Modal */}
      <Modal
        title="Unit Converter"
        open={isConversionModalVisible}
        onCancel={() => {
          setIsConversionModalVisible(false);
          setConversionResult(null);
          conversionForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Alert
          message="Convert Between Units"
          description="Convert quantities between different units. The system automatically handles hierarchical conversions."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form form={conversionForm} layout="vertical" onFinish={handleConversion}>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="from_unit_id"
                label="From Unit"
                rules={[{ required: true, message: 'Please select source unit' }]}
              >
                <Select
                  placeholder="Select source unit"
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {units.map((unit) => (
                    <Option key={unit.id} value={unit.id}>
                      {unit.name} ({unit.symbol})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="to_unit_id"
                label="To Unit"
                rules={[{ required: true, message: 'Please select target unit' }]}
              >
                <Select
                  placeholder="Select target unit"
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {units.map((unit) => (
                    <Option key={unit.id} value={unit.id}>
                      {unit.name} ({unit.symbol})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="quantity"
            label="Quantity"
            rules={[{ required: true, message: 'Please enter quantity' }]}
          >
            <InputNumber
              placeholder="Enter quantity to convert"
              min={0}
              step={0.01}
              precision={4}
              style={{ width: '100%' }}
            />
          </Form.Item>

          {conversionResult && (
            <Alert
              message="Conversion Result"
              description={
                <div>
                  <p>
                    <strong>
                      {conversionResult.original_quantity} {conversionResult.from_unit}
                    </strong>{' '}
                    ={' '}
                    <strong>
                      {conversionResult.converted_quantity} {conversionResult.to_unit}
                    </strong>
                  </p>
                  <p style={{ fontSize: '14px', color: '#666' }}>
                    {conversionResult.conversion_path}
                  </p>
                </div>
              }
              type="success"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}

          <Form.Item style={{ marginBottom: 0, textAlign: 'right', marginTop: 16 }}>
            <Space>
              <Button
                onClick={() => {
                  setIsConversionModalVisible(false);
                  setConversionResult(null);
                  conversionForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={convertUnitsMutation.isLoading}>
                Convert
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Units;
